import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Banner from '../Banner/Banner';
import BlackFridayBanner from '../BlackFridayBanner/BlackFridayBanner';
import HomeAdBanner from '../HomeAdBanner/HomeAdBanner';

export interface BannerData {
  id: string;
  type?: 'banner' | 'blackfriday' | 'homead';
  title: string;
  description?: string;
  secondaryText?: string;
  imageUrl?: string;
  primaryAction: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  disabled?: boolean;
}

interface BannerSliderProps {
  banners: BannerData[];
  autoPlay?: boolean;
  interval?: number;
  showNavigation?: boolean;
  showArrows?: boolean;
  className?: string;
  onSlideVisible?: (banner: BannerData) => void;
}

const BannerSlider: React.FC<BannerSliderProps> = ({
  banners,
  autoPlay = true,
  interval = 8000,
  showNavigation = true,
  showArrows = true,
  className = '',
  onSlideVisible,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
  }, [banners.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + banners.length) % banners.length);
  }, [banners.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  useEffect(() => {
    if (!autoPlay || isPaused || banners.length <= 1) return;

    const timer = setInterval(() => {
      nextSlide();
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, isPaused, nextSlide, banners.length]);

  useEffect(() => {
    if (!onSlideVisible || banners.length === 0) {
      return;
    }
    const banner = banners[currentIndex];
    if (banner) {
      onSlideVisible(banner);
    }
  }, [banners, currentIndex, onSlideVisible]);

  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="overflow-x-hidden">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {banners.map((banner) => (
            <div key={banner.id} className="w-full flex-shrink-0">
              {banner.type === 'homead' && banner.imageUrl ? (
                <HomeAdBanner
                  title={banner.title}
                  imageUrl={banner.imageUrl}
                  primaryAction={banner.primaryAction}
                  disabled={banner.disabled}
                />
              ) : banner.type === 'blackfriday' ? (
                <BlackFridayBanner
                  title={banner.title}
                  description={banner.description}
                  secondaryText={banner.secondaryText}
                  primaryAction={banner.primaryAction}
                  secondaryAction={banner.secondaryAction}
                  disabled={banner.disabled}
                />
              ) : (
                <Banner
                  title={banner.title}
                  description={banner.description || ''}
                  secondaryText={banner.secondaryText}
                  primaryAction={banner.primaryAction}
                  secondaryAction={banner.secondaryAction}
                  disabled={banner.disabled}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {showArrows && banners.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/30"
            aria-label="Previous banner"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/30"
            aria-label="Next banner"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      {showNavigation && banners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 space-x-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2.5 w-2.5 rounded-full transition-all duration-200 ${
                index === currentIndex ? 'scale-110 bg-white' : 'bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
